import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { apiClient } from '../../lib/apiClient'
import { subscriptionService, PLAN_LABELS, PLAN_PRICES, SubscriptionTier } from '../../services/subscriptionService'
import { DowngradeComplianceModal } from './DowngradeComplianceModal'
import s from './shared.module.css'

interface SubscriptionChange {
  id: string
  requested_tier: string
  change_type: string
  effective_date: string
  status: string
  created_at: string
}

interface PlanChangePreview {
  currentTier: string
  requestedTier: string
  currentPrice: number
  requestedPrice: number
  priceDifference: number
  isUpgrade: boolean
  chargeToday: number
  effectiveDate: string
  currentPeriodEnd: string
  newPeriodStart: string
  newPeriodEnd: string
}

const tierOrder: Record<string, number> = { trial: -1, starter: 0, professional: 1, enterprise: 2 }

export default function SubscriptionManager() {
  const { organization } = useAuth()
  const { subscription, limits, refresh: refreshSubscription } = useSubscription()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [scheduledChange, setScheduledChange] = useState<SubscriptionChange | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null)
  const [allLimits, setAllLimits] = useState<any[]>([])
  const [showComplianceModal, setShowComplianceModal] = useState(false)
  const [orgUsage, setOrgUsage] = useState<any>(null)
  const [planPreview, setPlanPreview] = useState<PlanChangePreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    loadScheduledChange()
    loadAllLimits()
    loadOrgUsage()

    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    if (checkout === 'success') {
      setMessage('Payment successful! Your plan has been activated.')
      const url = new URL(window.location.href)
      url.searchParams.delete('checkout')
      url.searchParams.delete('tier')
      window.history.replaceState({}, '', url.toString())
    } else if (checkout === 'cancel') {
      setError('Checkout was cancelled. Your plan has not changed.')
      const url = new URL(window.location.href)
      url.searchParams.delete('checkout')
      window.history.replaceState({}, '', url.toString())
    }
  }, [organization])

  const loadScheduledChange = async () => {
    if (!organization) return
    try {
      const data = await apiClient.get<SubscriptionChange | null>(
        `/orgs/${organization.id}/subscription/scheduled-change`
      )
      setScheduledChange(data)
    } catch {
      setScheduledChange(null)
    }
  }

  const loadAllLimits = async () => {
    const data = await subscriptionService.getAllLimits()
    if (data) setAllLimits(data)
  }

  const loadOrgUsage = async () => {
    if (!organization) return
    try {
      const data = await apiClient.get<any>(`/orgs/${organization.id}/usage`)
      if (data) setOrgUsage(data)
    } catch (err) {
      console.error('Failed to load usage:', err)
    }
  }

  const handleChangePlan = async (tier: SubscriptionTier) => {
    if (tier === subscription?.tier) return

    const currentTier = subscription?.tier || 'starter'
    const isDowngrade = tierOrder[tier] < tierOrder[currentTier as keyof typeof tierOrder]

    setSelectedTier(tier)
    setError('')
    setMessage('')
    setPlanPreview(null)

    if (organization) {
      setPreviewLoading(true)
      try {
        const preview = await apiClient.get<PlanChangePreview>(
          `/orgs/${organization.id}/subscription/change-preview?requestedTier=${tier}`
        )
        setPlanPreview(preview)
      } catch {
        // Non-fatal — modal still shows without preview
      } finally {
        setPreviewLoading(false)
      }
    }

    if (isDowngrade) {
      setShowComplianceModal(true)
    } else {
      setShowConfirmModal(true)
    }
  }

  const handleComplianceComplete = () => {
    setShowComplianceModal(false)
    setShowConfirmModal(true)
  }

  const confirmChangePlan = async () => {
    if (!selectedTier || !organization) return

    setLoading(true)
    setError('')
    setMessage('')

    try {
      const currentTier = subscription?.tier || 'starter'
      const isUpgrade = tierOrder[selectedTier] > tierOrder[currentTier as keyof typeof tierOrder]

      let result: any
      if (isUpgrade) {
        result = await subscriptionService.upgradePlan(organization.id, selectedTier)
      } else {
        result = await subscriptionService.schedulePlanChange(organization.id, selectedTier)
      }

      if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl
        return
      }

      const successMsg = result?.message || `Plan successfully changed to ${PLAN_LABELS[selectedTier]}`
      setMessage(successMsg)
      await loadScheduledChange()
      await refreshSubscription()
    } catch (err: any) {
      setError(err.message || 'Failed to change plan')
    } finally {
      setLoading(false)
      setShowConfirmModal(false)
      setSelectedTier(null)
      setPlanPreview(null)
    }
  }

  const handleCancelScheduledChange = async () => {
    if (!scheduledChange) return

    setLoading(true)
    setError('')
    setMessage('')

    try {
      await subscriptionService.cancelScheduledChange(organization.id)
      setMessage('Scheduled plan change cancelled')
      setScheduledChange(null)
      await refreshSubscription()
    } catch (err: any) {
      setError(err.message || 'Failed to cancel scheduled change')
    } finally {
      setLoading(false)
    }
  }

  const handleManageBilling = async () => {
    if (!organization) return

    setLoading(true)
    setError('')

    try {
      const result = await subscriptionService.createCustomerPortalSession(organization.id, window.location.href)
      window.location.href = result.url
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal')
    } finally {
      setLoading(false)
    }
  }

  const currentTier = subscription?.tier || 'starter'

  return (
    <div>
      {message && (
        <div style={{
          padding: '12px 16px',
          background: '#dff6dd',
          color: '#107c10',
          borderRadius: 4,
          marginBottom: 16,
          fontSize: 14
        }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fde7e9',
          color: '#d13438',
          borderRadius: 4,
          marginBottom: 16,
          fontSize: 14
        }}>
          {error}
        </div>
      )}

      {scheduledChange && (
        <div style={{
          padding: '16px',
          background: '#fff4ce',
          borderLeft: '4px solid #ffd335',
          borderRadius: 4,
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#323130', marginBottom: 4 }}>
            Plan change scheduled
          </div>
          <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 12 }}>
            Your plan will change to <strong>{PLAN_LABELS[scheduledChange.requested_tier as SubscriptionTier]}</strong> on{' '}
            {new Date(scheduledChange.effective_date).toLocaleDateString()}
          </div>
          <button
            onClick={handleCancelScheduledChange}
            disabled={loading}
            style={{
              padding: '6px 12px',
              background: '#fff',
              border: '1px solid #c8c6c4',
              borderRadius: 3,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel scheduled change
          </button>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
        marginBottom: 24,
      }}>
        {(['starter', 'professional', 'enterprise'] as SubscriptionTier[]).map(tier => {
          const lim = allLimits.find(l => l.tier === tier)
          const isCurrent = currentTier === tier

          return (
            <div
              key={tier}
              style={{
                border: isCurrent ? '2px solid #0078d4' : '1px solid #edebe9',
                borderRadius: 4,
                padding: 20,
                background: '#fff',
                position: 'relative',
              }}
            >
              {isCurrent && (
                <span style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  padding: '4px 12px',
                  background: '#0078d4',
                  color: '#fff',
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 600,
                }}>
                  Current
                </span>
              )}

              <div style={{ fontSize: 20, fontWeight: 600, color: '#323130', marginBottom: 8 }}>
                {PLAN_LABELS[tier]}
              </div>

              <div style={{ fontSize: 32, fontWeight: 700, color: '#323130', marginBottom: 4 }}>
                ${PLAN_PRICES[tier]}
              </div>

              <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 20 }}>
                per month
              </div>

              {lim && (
                <div style={{ marginBottom: 20 }}>
                  {[
                    lim.max_users === -1 ? 'Unlimited users' : `Up to ${lim.max_users} users`,
                    lim.max_projects === -1 ? 'Unlimited projects' : `Up to ${lim.max_projects} projects`,
                    lim.has_gantt ? 'Gantt + Forecasting' : null,
                    lim.has_partner_rates ? 'Partner rate sheets' : null,
                    lim.has_sso ? 'SSO (Azure/Google)' : null,
                    lim.has_audit_log ? 'Audit logging' : null,
                  ].filter(Boolean).map((feature, i) => (
                    <div key={i} style={{
                      fontSize: 13,
                      color: '#605e5c',
                      marginBottom: 6,
                      paddingLeft: 16,
                      position: 'relative',
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: '#107c10',
                        fontWeight: 700,
                      }}>✓</span>
                      {feature}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => handleChangePlan(tier)}
                disabled={loading || isCurrent}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: isCurrent ? '#f3f2f1' : '#0078d4',
                  color: isCurrent ? '#a19f9d' : '#fff',
                  border: 'none',
                  borderRadius: 3,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isCurrent ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {isCurrent ? 'Current plan' : 'Select plan'}
              </button>
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 24 }}>
        <button
          onClick={handleManageBilling}
          disabled={loading}
          style={{
            padding: '10px 20px',
            background: '#fff',
            border: '1px solid #c8c6c4',
            borderRadius: 3,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {loading ? 'Loading...' : 'Manage payment methods'}
        </button>
      </div>

      {showComplianceModal && selectedTier && (
        <DowngradeComplianceModal
          orgId={organization!.id}
          currentTier={currentTier}
          targetTier={selectedTier}
          onClose={() => {
            setShowComplianceModal(false)
            setSelectedTier(null)
          }}
          onCompliant={handleComplianceComplete}
        />
      )}

      {showConfirmModal && selectedTier && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 4,
            padding: 24,
            maxWidth: 480,
            width: '90%',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, color: '#323130' }}>
              Confirm plan change
            </h3>

            <p style={{ fontSize: 14, color: '#605e5c', marginBottom: previewLoading || planPreview ? 16 : 20 }}>
              {tierOrder[selectedTier] > tierOrder[currentTier as keyof typeof tierOrder]
                ? `Your plan will be upgraded to ${PLAN_LABELS[selectedTier]} immediately.`
                : `Your plan will change to ${PLAN_LABELS[selectedTier]} at the end of your current billing period.`
              }
            </p>

            {previewLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f3f2f1', borderRadius: 4, marginBottom: 20, fontSize: 13, color: '#605e5c' }}>
                <div style={{ width: 16, height: 16, border: '2px solid #edebe9', borderTopColor: '#0078d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                Loading billing preview...
              </div>
            )}

            {!previewLoading && planPreview && (
              <div style={{ padding: '16px', background: '#f3f2f1', borderRadius: 4, marginBottom: 20, fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: '#323130', marginBottom: 12, fontSize: 14 }}>Billing summary</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {planPreview.isUpgrade ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#323130' }}>
                        <span>Charged today (prorated)</span>
                        <strong>${planPreview.chargeToday.toFixed(2)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                        <span>New monthly rate</span>
                        <span>${planPreview.requestedPrice}/mo</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                        <span>Next full billing cycle</span>
                        <span>{new Date(planPreview.currentPeriodEnd).toLocaleDateString()} — {new Date(planPreview.newPeriodEnd).toLocaleDateString()}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#323130' }}>
                        <span>Downgrade takes effect</span>
                        <strong>{new Date(planPreview.effectiveDate).toLocaleDateString()}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                        <span>You keep current features until</span>
                        <span>{new Date(planPreview.currentPeriodEnd).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                        <span>New monthly rate (from {new Date(planPreview.newPeriodStart).toLocaleDateString()})</span>
                        <span>${planPreview.requestedPrice}/mo</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#605e5c' }}>
                        <span>Charged today</span>
                        <span>$0.00</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowConfirmModal(false)
                  setSelectedTier(null)
                  setPlanPreview(null)
                }}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  border: '1px solid #c8c6c4',
                  borderRadius: 3,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmChangePlan}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#0078d4',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 3,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {loading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
